import React, { useState } from "react";
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Alert, Table } from "reactstrap";
import { useCart } from '../context/cartContext';
import axios from "axios";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { FiCheckCircle, FiLock, FiMapPin, FiShield } from "react-icons/fi";
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import Helmet from "../components/Helmet/Helmet";
import { getApiUrl, siteConfig } from "../utils/site";
const DefaultIcon = L.icon({ iconUrl, shadowUrl: iconShadow });
L.Marker.prototype.options.icon = DefaultIcon;

const checkoutHighlights = [
  { icon: <FiShield />, title: "Verified sourcing", text: "Every order is presented with stronger trust and authenticity cues." },
  { icon: <FiLock />, title: "Protected payment", text: "Orders are sent to secure payment after the address review step." },
  { icon: <FiMapPin />, title: "Precise delivery", text: "Pin the destination so fulfilment and delivery support stay accurate." },
];

const Checkout = () => {
  const { items, clearCart } = useCart();
  const [address, setAddress] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    location: { lat: '', lng: '' },
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const total = items.reduce((sum, i) => sum + i.product.price * i.quantity, 0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddress((prev) => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setAddress((prev) => ({ ...prev, location: { ...prev.location, [name]: value } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));
      const orderRes = await axios.post(getApiUrl('/api/orders'), {
        items: items.map(i => ({ product_id: i.product.id ?? i.product.uuid, quantity: i.quantity })),
        delivery: {
          address_line1: address.address_line1,
          address_line2: address.address_line2,
          city: address.city,
          state: address.state,
          postal_code: address.postal_code,
          country: address.country,
          location: {
            lat: address.location.lat,
            lng: address.location.lng,
          },
        },
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      clearCart();
      setSuccess('Order placed successfully! Redirecting to payment...');
      // Initiate payment with Chapa
      const paymentRes = await axios.post(getApiUrl('/api/payments/initiate'), {
        order_id: orderRes.data.order.hash_id || orderRes.data.order.id,
        customer: {
          email: user.email,
          first_name: user.name || user.full_name || user.username || '',
          last_name: user.last_name || '',
        }
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      window.location.href = paymentRes.data.redirect_url;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to place order.');
    } finally {
      setLoading(false);
    }
  };

  // Map click handler for selecting location
  function LocationMarker() {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        setAddress((prev) => ({ ...prev, location: { lat, lng } }));
      },
    });
    return address.location.lat && address.location.lng ? (
      <Marker position={[address.location.lat, address.location.lng]} />
    ) : null;
  }

  return (
    <>
      <Helmet
        title="Secure Checkout | Roha Jewellery Ethiopia"
        description="Complete your Roha Jewellery order with a more professional Ethiopian gemstone checkout experience, secure payment handoff, and precise delivery details."
        keywords="secure checkout Ethiopia, gemstone checkout Ethiopia, jewellery checkout Ethiopia, buy gemstone in Ethiopia, buy jewellery in Ethiopia, Roha Jewellery checkout"
        canonical={`${siteConfig.url}/checkout`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CheckoutPage",
          name: "Roha Jewellery Checkout",
          url: `${siteConfig.url}/checkout`,
          description: "Secure checkout experience for Roha Jewellery orders.",
          isPartOf: siteConfig.url,
        }}
      />

      <main className="roha-page">
        <section className="roha-page-hero roha-page-hero--compact">
          <Container>
            <div className="roha-page-hero__grid">
              <div>
                <p className="roha-eyebrow">Secure checkout</p>
                <h1>Complete your order with a calmer, more premium experience.</h1>
                <p>
                  Review your delivery details, confirm your jewellery selection, and continue to
                  secure payment with clearer trust signals and a more professional layout.
                </p>
              </div>
              <div className="roha-checkout__hero-card">
                {checkoutHighlights.map((item) => (
                  <article key={item.title} className="roha-checkout__hero-point">
                    <span>{item.icon}</span>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.text}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </Container>
        </section>

        <section className="roha-section">
          <Container>
            {error && <Alert color="danger">{error}</Alert>}
            {success && <Alert color="success">{success}</Alert>}

            <Row className="g-4 align-items-start">
              <Col xl="7" lg="8">
                <Form onSubmit={handleSubmit} className="roha-checkout-card">
                  <div className="roha-checkout-card__header">
                    <div>
                      <p className="roha-eyebrow">Delivery details</p>
                      <h2>Tell us where to deliver your jewellery</h2>
                    </div>
                    <span className="roha-checkout-card__step"><FiCheckCircle /> Step 1 of 2</span>
                  </div>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Address Line 1 *</Label>
                        <Input name="address_line1" value={address.address_line1} onChange={handleChange} required />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Address Line 2</Label>
                        <Input name="address_line2" value={address.address_line2} onChange={handleChange} />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label>City *</Label>
                        <Input name="city" value={address.city} onChange={handleChange} required />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>State / Region *</Label>
                        <Input name="state" value={address.state} onChange={handleChange} required />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Postal Code *</Label>
                        <Input name="postal_code" value={address.postal_code} onChange={handleChange} required />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Country *</Label>
                        <Input name="country" value={address.country} onChange={handleChange} required />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Latitude</Label>
                        <Input name="lat" value={address.location.lat} onChange={handleLocationChange} type="number" step="any" />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Longitude</Label>
                        <Input name="lng" value={address.location.lng} onChange={handleLocationChange} type="number" step="any" />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="roha-checkout-map">
                    <div className="roha-checkout-map__copy">
                      <strong>Pin the exact destination</strong>
                      <p>Click the map so our delivery team can verify the final drop-off point faster.</p>
                    </div>
                    <div style={{ height: 280, width: "100%", borderRadius: 24, overflow: "hidden" }}>
                      <MapContainer
                        center={[9.03, 38.74]}
                        zoom={13}
                        style={{ height: "100%", width: "100%" }}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <LocationMarker />
                      </MapContainer>
                    </div>
                  </div>

                  <div className="roha-checkout-card__footer">
                    <div>
                      <strong>Next step</strong>
                      <p>You will be redirected to secure payment after the order is created.</p>
                    </div>
                    <Button color="primary" type="submit" size="lg" disabled={loading || items.length === 0} className="rounded-pill px-5">
                      {loading ? 'Placing Order...' : 'Place Order & Pay'}
                    </Button>
                  </div>
                </Form>
              </Col>

              <Col xl="5" lg="4">
                <aside className="roha-checkout-summary">
                  <div className="roha-checkout-summary__header">
                    <p className="roha-eyebrow">Order summary</p>
                    <h2>Your selected pieces</h2>
                    <p>{items.length} item(s) prepared for checkout.</p>
                  </div>

                  <div className="roha-checkout-summary__list">
                    {items.map((i) => (
                      <div key={i.product.id ?? i.product.uuid} className="roha-checkout-summary__item">
                        <div>
                          <strong>{i.product.name}</strong>
                          <p>Qty {i.quantity}</p>
                        </div>
                        <span>ETB {i.product.price * i.quantity}</span>
                      </div>
                    ))}
                  </div>

                  <Table borderless responsive className="roha-checkout-table">
                    <tbody>
                      {items.map(i => (
                        <tr key={`summary-${i.product.id ?? i.product.uuid}`}>
                          <td>{i.product.name}</td>
                          <td className="text-end">ETB {i.product.price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <div className="roha-checkout-summary__total">
                    <span>Total</span>
                    <strong>ETB {total}</strong>
                  </div>

                  <div className="roha-checkout-summary__notes">
                    <p><FiShield /> Authentic gemstone and jewellery presentation</p>
                    <p><FiLock /> Secure payment continuation</p>
                    <p><FiMapPin /> Delivery coordination from Ethiopia to local or global destinations</p>
                  </div>
                </aside>
              </Col>
            </Row>
          </Container>
        </section>
      </main>
    </>
  );
};

export default Checkout;


















